import {useCallback, useEffect, useRef, useState} from "react";

// Global state to manage BTRenderer script loading and instances
class BetbyWidgetManager {
  constructor() {
    this.scriptLoaded = false;
    this.scriptLoading = false;
    this.scriptCallbacks = [];
    this.activeWidgets = new Map();
    this.initPromise = null;
  }

  async ensureScriptLoaded() {
    if (this.scriptLoaded) {
      return Promise.resolve();
    }

    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = new Promise((resolve, reject) => {
      const existingScript = document.querySelector('script[src="https://raspaygana.sptpub.com/bt-renderer.min.js"]');

      if (existingScript && window.BTRenderer) {
        this.scriptLoaded = true;
        resolve();
        return;
      }

      if (existingScript && !window.BTRenderer) {
        // Script exists but not ready, wait for it
        const checkReady = () => {
          if (window.BTRenderer) {
            this.scriptLoaded = true;
            resolve();
          } else {
            setTimeout(checkReady, 100);
          }
        };
        checkReady();
        return;
      }

      // Load new script
      const scriptElement = document.createElement("script");
      scriptElement.src = "https://raspaygana.sptpub.com/bt-renderer.min.js";
      scriptElement.async = true;

      scriptElement.onload = () => {
        if (window.BTRenderer) {
          this.scriptLoaded = true;
          resolve();
        } else {
          reject(new Error("BTRenderer not available after script load"));
        }
      };

      scriptElement.onerror = () => {
        reject(new Error("Failed to load BTRenderer script"));
      };

      document.head.appendChild(scriptElement);
    });

    return this.initPromise;
  }

  async createWidget(widgetId, config) {
    try {
      await this.ensureScriptLoaded();

      // Clean up any existing widget with this ID first
      this.destroyWidget(widgetId);

      // Clear the target container completely
      if (config.target) {
        // Stop any running operations in the container
        const scripts = config.target.querySelectorAll("script");
        scripts.forEach(script => {
          if (script.parentNode) {
            script.parentNode.removeChild(script);
          }
        });

        // Clear all content
        config.target.innerHTML = "";

        // Reset any data attributes that might interfere
        config.target.removeAttribute("data-bt-initialized");
        config.target.removeAttribute("data-bt-widget-id");
        config.target.removeAttribute("data-widget-ready");
      }

      // Give more time for cleanup to complete, especially if kill() was called
      await new Promise(resolve => setTimeout(resolve, 200));

      // Verify window.BTRenderer is still available
      if (!window.BTRenderer) {
        throw new Error("BTRenderer not available");
      }

      const widgetInstance = new window.BTRenderer();

      // Store the widget instance immediately with metadata
      this.activeWidgets.set(widgetId, {
        instance: widgetInstance,
        target: config.target,
        initialized: false,
        widgetName: config.widgetName,
        createdAt: Date.now(),
      });

      // Add error handling wrapper to the config
      const wrappedConfig = {
        ...config,
        onError: error => {
          // Clean up failed widget using kill() method
          this.destroyWidget(widgetId);
          // Call original error handler
          if (config.onError) {
            config.onError(error);
          }
        },
      };

      // Initialize the widget - this is where BTRenderer takes control
      widgetInstance.initialize(wrappedConfig);

      // Mark as initialized and store reference for potential kill() calls
      const widgetData = this.activeWidgets.get(widgetId);
      if (widgetData) {
        widgetData.initialized = true;
        widgetData.initializedAt = Date.now();
      }

      return widgetInstance;
    } catch (error) {
      // Clean up on failure - this will call kill() if instance was created
      this.destroyWidget(widgetId);
      throw error;
    }
  }

  destroyWidget(widgetId) {
    const widget = this.activeWidgets.get(widgetId);
    if (widget) {
      try {
        // First priority: Use BTRenderer's kill() method - this is the official way to destroy instances
        if (widget.instance && typeof widget.instance.kill === "function") {
          try {
            // Create a promise to handle the async nature of kill()
            const killPromise = new Promise(resolve => {
              try {
                widget.instance.kill();
                resolve();
              } catch (_killError) {
                // Even if kill() throws, we consider it "completed" to proceed with cleanup
                resolve();
              }
            });

            // Don't wait for kill() to complete to avoid blocking cleanup
            // BTRenderer.kill() is async and might conflict with React's cleanup timing
            killPromise.catch(_e => {});
          } catch (_e) {
            // If kill() fails, try fallback methods
            if (typeof widget.instance.destroy === "function") {
              try {
                widget.instance.destroy();
              } catch (_destroyError) {}
            }
          }
        } else if (widget.instance) {
          // Fallback cleanup for instances without kill() method

          if (typeof widget.instance.destroy === "function") {
            try {
              widget.instance.destroy();
            } catch (_e) {}
          }

          if (typeof widget.instance.cleanup === "function") {
            try {
              widget.instance.cleanup();
            } catch (_e) {}
          }
        }

        // Clean up the DOM content after widget instance cleanup
        if (widget.target) {
          try {
            // Check if the target element is still in the DOM before manipulating it
            if (widget.target.isConnected || document.contains(widget.target)) {
              // Clear any remaining content that BTRenderer.kill() might have missed
              widget.target.innerHTML = "";

              // Remove any data attributes that might cause conflicts
              widget.target.removeAttribute("data-bt-initialized");
              widget.target.removeAttribute("data-bt-widget-id");
              widget.target.removeAttribute("data-widget-ready");

              // Reset any inline styles that might have been added
              widget.target.style.cssText = "";
            } else {
            }
          } catch (_e) {}
        }
      } catch (_error) {
      } finally {
        // Always remove from active widgets tracking
        this.activeWidgets.delete(widgetId);
      }
    } else {
    }
  }

  destroyAllWidgets() {
    // Create a copy of the keys to avoid iteration issues during deletion
    const widgetIds = Array.from(this.activeWidgets.keys());

    for (const widgetId of widgetIds) {
      this.destroyWidget(widgetId);
    }

    // Ensure the map is completely cleared
    this.activeWidgets.clear();
  }

  // Utility method to get info about active widgets (useful for debugging)
  getActiveWidgetsInfo() {
    const widgets = [];
    this.activeWidgets.forEach((widget, widgetId) => {
      widgets.push({
        id: widgetId,
        widgetName: widget.widgetName,
        initialized: widget.initialized,
        createdAt: widget.createdAt,
        initializedAt: widget.initializedAt,
        hasKillMethod: widget.instance && typeof widget.instance.kill === "function",
      });
    });
    return widgets;
  }
}

// Global singleton instance
const widgetManager = new BetbyWidgetManager();

/**
 * Hook for managing Betby widgets with proper cleanup and conflict prevention
 */
export const useBetbyWidgetManager = widgetConfig => {
  const containerRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const widgetIdRef = useRef(null);
  const initTimeoutRef = useRef(null);
  const initializingRef = useRef(false);
  const configHashRef = useRef(null);

  // Create a stable hash of the widget config to prevent unnecessary re-initializations
  const getConfigHash = useCallback(config => {
    if (!config) return null;
    return JSON.stringify({
      token: config.token,
      brand_id: config.brand_id,
      lang: config.lang,
      widgetName: config.widgetName,
      themeName: config.themeName,
    });
  }, []);

  // Generate stable widget ID based on widget name only
  const generateWidgetId = useCallback(() => {
    return `betby-widget-${widgetConfig?.widgetName || "unknown"}-${Math.random().toString(36).substr(2, 9)}`;
  }, [widgetConfig?.widgetName]);

  const createWidget = useCallback(async () => {
    if (!widgetConfig || !containerRef.current || isLoading || initializingRef.current) {
      return;
    }

    // Check if config actually changed
    const newConfigHash = getConfigHash(widgetConfig);
    if (newConfigHash === configHashRef.current && isInitialized) {
      return; // No need to reinitialize
    }

    initializingRef.current = true;
    setIsLoading(true);
    setError(null);

    try {
      // Clean up existing widget first
      if (widgetIdRef.current) {
        widgetManager.destroyWidget(widgetIdRef.current);
        widgetIdRef.current = null;
        setIsInitialized(false);
      }

      // Wait a bit for cleanup to complete
      await new Promise(resolve => setTimeout(resolve, 100));

      // Generate new widget ID for this instance
      const widgetId = generateWidgetId();
      widgetIdRef.current = widgetId;

      // Create the widget configuration with the container as target
      const config = {
        ...widgetConfig,
        target: containerRef.current,
        widgetParams: {
          ...widgetConfig.widgetParams,
          onError: error => {
            setError(`Widget error: ${error.message || error}. Please try refreshing the page.`);
            // Call original onError if it exists
            if (widgetConfig.widgetParams?.onError) {
              widgetConfig.widgetParams.onError(error);
            }
          },
        },
      };

      await widgetManager.createWidget(widgetId, config);

      configHashRef.current = newConfigHash;
      setIsInitialized(true);
    } catch (err) {
      setError(err.message || "Failed to initialize widget");
    } finally {
      setIsLoading(false);
      initializingRef.current = false;
    }
  }, [widgetConfig, isLoading, isInitialized, getConfigHash, generateWidgetId]);

  const destroyWidget = useCallback(() => {
    // Clear any pending initialization
    if (initTimeoutRef.current) {
      clearTimeout(initTimeoutRef.current);
      initTimeoutRef.current = null;
    }

    if (widgetIdRef.current) {
      // Use the manager's destroy method which properly calls kill()
      widgetManager.destroyWidget(widgetIdRef.current);
      widgetIdRef.current = null;
      setIsInitialized(false);
      configHashRef.current = null;
    }

    initializingRef.current = false;
  }, []);

  // Initialize widget when config changes - using a more controlled approach
  useEffect(() => {
    if (!widgetConfig || !containerRef.current) {
      return;
    }

    // Clear any existing timeout
    if (initTimeoutRef.current) {
      clearTimeout(initTimeoutRef.current);
    }

    // Debounce the initialization to prevent rapid re-renders
    initTimeoutRef.current = setTimeout(() => {
      const newConfigHash = getConfigHash(widgetConfig);
      if (newConfigHash !== configHashRef.current || !isInitialized) {
        createWidget();
      }
    }, 250); // Increased debounce time

    return () => {
      if (initTimeoutRef.current) {
        clearTimeout(initTimeoutRef.current);
      }
    };
  }, [widgetConfig, createWidget, getConfigHash, isInitialized]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (initTimeoutRef.current) {
        clearTimeout(initTimeoutRef.current);
      }
      destroyWidget();
    };
  }, [destroyWidget]);

  const retryInitialization = useCallback(() => {
    if (initializingRef.current) {
      return; // Already initializing
    }

    destroyWidget();
    // Add delay before retry to allow cleanup to complete
    setTimeout(() => {
      if (widgetConfig && containerRef.current) {
        createWidget();
      }
    }, 300);
  }, [createWidget, destroyWidget, widgetConfig]);

  // Debug method to check widget manager state
  const getDebugInfo = useCallback(() => {
    return {
      isLoading,
      error,
      isInitialized,
      widgetId: widgetIdRef.current,
      isInitializing: initializingRef.current,
      configHash: configHashRef.current,
      activeWidgets: widgetManager.getActiveWidgetsInfo(),
    };
  }, [isLoading, error, isInitialized]);

  return {
    containerRef,
    isLoading,
    error,
    isInitialized,
    retryInitialization,
    setError,
    getDebugInfo, // Available for debugging widget issues
  };
};

export default useBetbyWidgetManager;
